/**
 * MaternityTimeline.jsx
 * 
 * Visual timeline showing all maternity events chronologically.
 * Styled with MUI components.
 */

import React from 'react';
import {
    Box,
    Typography,
    Chip
} from '@mui/material';
import {
    Event as EventIcon,
    Notifications as NotifyIcon,
    PlayArrow as StartIcon,
    CheckCircle as CheckIcon,
    Warning as WarningIcon,
    ChildCare as BirthIcon,
    WorkOff as KITIcon,
    Email as EmailIcon,
    ArrowForward as ReturnIcon
} from '@mui/icons-material';

const MaternityTimeline = ({ record }) => {
    if (!record) return null;

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const isDatePast = (dateStr) => {
        if (!dateStr) return false;
        return new Date(dateStr) < new Date();
    };

    const isDateToday = (dateStr) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        const today = new Date();
        return date.toDateString() === today.toDateString();
    };

    // Build timeline events
    const events = [
        {
            date: record.notification_date,
            label: 'Notification Received',
            description: 'Employee formally notified of pregnancy',
            icon: <NotifyIcon />,
            color: '#8b5cf6'
        },
        {
            date: record.response_deadline,
            label: '28-Day Response Deadline',
            description: 'Employer must respond by this date (legal requirement)',
            icon: <WarningIcon />,
            color: '#f59e0b',
            isDeadline: true
        },
        {
            date: record.earliest_start_date,
            label: 'Earliest Leave Start',
            description: '11 weeks before EWC — earliest maternity leave can begin',
            icon: <EventIcon />,
            color: '#64748b'
        },
        {
            date: record.ewc,
            label: 'Expected Week of Childbirth',
            description: 'Baby due date',
            icon: <BirthIcon />,
            color: '#ec4899'
        },
        {
            date: record.intended_start_date,
            label: 'Leave Start Date',
            description: 'Maternity leave begins',
            icon: <StartIcon />,
            color: '#22c55e'
        },
        ...(record.actual_birth_date ? [{
            date: record.actual_birth_date,
            label: 'Actual Birth Date',
            description: 'Baby was born',
            icon: <BirthIcon />,
            color: '#ec4899',
            highlight: true
        }] : []),
        ...(record.kit_days || []).map((kit) => ({
            date: kit.kit_date,
            label: 'KIT Day',
            description: `${kit.hours_worked} hours — ${kit.description || 'Keeping in touch day'}`,
            icon: <KITIcon />,
            color: '#3b82f6'
        })),
        {
            date: record.extension_deadline,
            label: 'Extension Deadline',
            description: '8 weeks before OML ends — last date to request extension',
            icon: <WarningIcon />,
            color: '#f59e0b',
            isDeadline: true
        },
        {
            date: record.oml_end_date,
            label: 'OML Ends',
            description: 'Ordinary Maternity Leave ends (26 weeks)',
            icon: <CheckIcon />,
            color: '#3b82f6'
        },
        {
            date: record.aml_end_date,
            label: 'AML Ends',
            description: 'Additional Maternity Leave ends (52 weeks)',
            icon: <CheckIcon />,
            color: '#3b82f6'
        },
        {
            date: record.expected_return_date,
            label: 'Expected Return',
            description: 'Employee expected to return to work',
            icon: <ReturnIcon />,
            color: '#22c55e'
        }
    ].filter(e => e.date).sort((a, b) => new Date(a.date) - new Date(b.date));

    return (
        <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Timeline
            </Typography>
            
            <Box sx={{ position: 'relative' }}>
                {events.map((event, index) => {
                    const isPast = isDatePast(event.date);
                    const isToday = isDateToday(event.date);
                    
                    return (
                        <Box 
                            key={index}
                            sx={{ 
                                display: 'flex', 
                                gap: 3, 
                                mb: 3,
                                position: 'relative',
                                '&::before': index < events.length - 1 ? {
                                    content: '""',
                                    position: 'absolute',
                                    left: 20,
                                    top: 48,
                                    width: 2,
                                    height: 'calc(100% - 20px)',
                                    bgcolor: isPast ? '#e5e7eb' : '#f3f4f6'
                                } : {}
                            }}
                        >
                            <Box 
                                sx={{ 
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    bgcolor: isPast ? `${event.color}20` : `${event.color}10`,
                                    color: isPast ? event.color : `${event.color}80`,
                                    border: isToday ? `2px solid ${event.color}` : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                            >
                                {event.icon}
                            </Box>
                            <Box sx={{ flex: 1, pt: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                    <Typography 
                                        variant="subtitle2" 
                                        sx={{ 
                                            fontWeight: 600,
                                            color: isPast ? 'text.primary' : 'text.secondary'
                                        }}
                                    >
                                        {event.label}
                                    </Typography>
                                    {event.isDeadline && (
                                        <Chip 
                                            label="Deadline" 
                                            size="small" 
                                            color="warning" 
                                            sx={{ height: 20, fontSize: 10 }}
                                        />
                                    )}
                                    {event.highlight && (
                                        <Chip 
                                            label="Actual" 
                                            size="small" 
                                            color="success" 
                                            sx={{ height: 20, fontSize: 10 }}
                                        />
                                    )}
                                    {isToday && (
                                        <Chip 
                                            label="Today" 
                                            size="small" 
                                            color="primary" 
                                            sx={{ height: 20, fontSize: 10, bgcolor: event.color }}
                                        />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    {event.description}
                                </Typography>
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        color: isPast ? 'text.secondary' : event.color,
                                        fontWeight: isToday ? 600 : 400
                                    }}
                                >
                                    {formatDate(event.date)}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
};

export default MaternityTimeline;